import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { Image, ScrollView, StyleSheet, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import styles from '../Styles';
import { useJourney } from '../contexts/JourneyContext';
import CloudBackground from '../elements/CloudBackground';
import SmallButton from '../elements/SmallButton';
import Toolbar from '../elements/Toolbar';

const ScreenLobby = (props) => {
  const [canContinue, setCanContinue] = useState(true);
  const [isFinished, setIsFinished] = useState(false);

  const journey = useJourney();
  
  const timerIdRef = useRef(null);

  useEffect(() => {
    timerIdRef.current = setTimeout(() => {
      checkAvailableLocations();
    }, 1000);

    return () => {
      if (timerIdRef.current) {
        clearTimeout(timerIdRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAvailableLocations = () => {
    hasAvailableLocations();
  };

 
  const hasAvailableLocations = async () => {
    const apiResponse = await journey.hasAvailableLocations();
    
    if (apiResponse === 'finished') {
      setCanContinue(false);
      setIsFinished(true);
    } else if (apiResponse === "yes") {
      setCanContinue(true);
    } else {
      setCanContinue(false);
      timerIdRef.current = setTimeout(() => {
        hasAvailableLocations();
      }, 10000);
    }
  };

  const nextTrip = () => {
    if (props.isChecking !== true) {
      journey.startNextLocation();
    }
  };

  // Helper function for ContinueButton
  const renderContinueButton = () => {
    if (canContinue === true) {
      return (
        <SmallButton
          buttonStyle={styles.nextTripButton}
          onPress={nextTrip}
          title="Keep Looking"
        />
      );
    } else {
      return (
        <Text style={localStyles.waitingText}>
          All locations are full at this time. You will be able to continue once one becomes available.
        </Text>
      );
    }
  };

  // Helper function for VisitedBox
  const getImageForLocation = (loc) => {    
    const namesMap = {
      "Art": require("../../assets/images/PlugArt.png"),
      "Authority": require("../../assets/images/PlugAuthority.png"),
      "Belief": require("../../assets/images/PlugBelief.png"),
      "Belonging": require("../../assets/images/PlugBelonging.png"),
      "Future": require("../../assets/images/PlugFuture.png"),
      "Loneliness": require("../../assets/images/PlugLoneliness.png"),
      "Love": require("../../assets/images/PlugLove.png"),
      "Nostalgia": require("../../assets/images/PlugNostalgia.png"),
      "Perspective": require("../../assets/images/PlugPerspective.png"),
      "Trauma": require("../../assets/images/PlugTrauma.png")
    };
    //console.log("Getting image for location icon: " + loc.name + " / " + loc.alt_name_1 + " / " + loc.icon);
    return namesMap[loc.icon || loc.alt_name_1] || namesMap["Art"];
  };

  const renderVisitedBox = (location, index) => {
    const image = getImageForLocation(location);
    if (image !== null) {
      // Show image when we have one
      return (
        <View key={index} style={localStyles.visitedImageContainer}>
          <Image 
            source={image}
            style={localStyles.visitedImage}
            resizeMode="contain"
          />
        </View>
      );
    } else {
      // Show text fallback when no image
      return (
        <Text key={index} style={styles.inBetweenText}>
          {location.name}
        </Text>
      );    
    }
  };

  // Create styles
  const localStyles = StyleSheet.create({
    waitingText: {
      color: '#ddd',
      textAlign: 'center',
      margin: 10,
      fontSize: 16,
      fontWeight: 'bold'
    },
    finishedText: {
      color: '#ddd',
      textAlign: 'center',
      margin: 20,
      fontSize: 18,
      fontWeight: 'bold'
    },
    instructionText: {
      color: '#ddd',
      textAlign: 'center',
      margin: 20,
      fontSize: 18,
      fontWeight: 'bold'
    },
    visitedContainer: {
      flex: 1,
      width: '100%',
      marginTop: 10,
      backgroundColor: 'rgba(80, 80, 80, 0.33)'
    },
    visitedHeaderText: {
      color: '#ddd',
      margin: 10,
      marginTop: 20,
      marginBottom: 10,
      fontSize: 18,
      fontWeight: 'bold',
      textAlign: 'center'
    },
    visitedImageContainer: {
      flex: 1
    },
    visitedImage: {
      flex: 1,
      width: "100%",
      height: 40
    },
    inBetweenFrame: {
      paddingTop: 100,
      flex: 1,
      height: '100%',
      flexDirection: 'column',
      alignItems: 'center',
    },
  });

  // Sort visited locations backwards
  const allVisited = [...journey.visitedLocationObjects()].reverse();

  if (isFinished === true) {
    let timeCaption = null;
    const currentTime = new Date();
    /* skipping the finale countdown until we figure out time better 

    const timeToFinale = props.finaleStartsAt - currentTime;
    const minuteLength = 1000 * 60;
    const minutesNum = Math.floor(timeToFinale / minuteLength);
    
    if (minutesNum <= 1) {
      timeCaption = "The final stage of your journey will begin very shortly.";
    } else {
      timeCaption = "The final stage of your journey will begin in " + minutesNum + " minutes.";
    }
      */
     timeCaption = "The final stage of your journey will begin very shortly.";

    return (
      <CloudBackground>
        <View style={localStyles.inBetweenFrame}>
          <Text style={localStyles.finishedText}>
            There are no more locations for you to explore.
          </Text>
          <Text style={localStyles.finishedText}>
            {timeCaption}
          </Text>
        </View>
        <Toolbar />
      </CloudBackground>
    );
  } else {
    return (
      <CloudBackground>
        <SafeAreaView style={{ flex: 1 }}>
          <View style={localStyles.inBetweenFrame}>
            <Text style={localStyles.instructionText}>
              You can take a break now or tap below to continue
            </Text>
            <View style={{ marginBottom: 20 }}>
              {renderContinueButton()}
            </View>
            {allVisited.length === 0 && (
              <Text style={localStyles.waitingText}>
                You have not visited any locations yet.
              </Text>
            )}
            {allVisited.length > 0 && (
              <>
                <View style={localStyles.visitedContainer}>
                  <Text style={localStyles.visitedHeaderText}>
                    You have experienced:
                  </Text>
                  <ScrollView style={{ flex: 1 }}>
                    {allVisited.map((location, index) => {
                      return renderVisitedBox(location, index);
                    })}
                  </ScrollView>
                </View>
              </> 
            )}
          </View>
          <Toolbar />
        </SafeAreaView>
      </CloudBackground>
    );
  }
};

// PropTypes validation
ScreenLobby.propTypes = {
  startNextTrip: PropTypes.func.isRequired,
  isChecking: PropTypes.bool,
};

export default ScreenLobby;
